import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useQueries } from '@tanstack/react-query';
import { GeoJsonLayer, ScatterplotLayer, PolygonLayer, TextLayer, PathLayer } from '@deck.gl/layers';
import { Layer, project32, picking } from '@deck.gl/core';
import GL from '@luma.gl/constants';
import { Model, Geometry } from '@luma.gl/engine';
import { getJSON } from './getJSON';
import cmapMako from './components/mako_colors.json';
import { crwColor } from './crwDailyProducts';

// Helper function to convert hex to rgb array for DeckGL
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [128, 128, 128];
};

const windColor = (speedMs) => {
  // Mirrors the YlOrRd palette used by the portal's GFS wind legend. Keep a
  // translucent alpha so the basemap and island landmasses remain readable.
  const stops = [
    [255, 255, 204], [255, 237, 160], [254, 217, 118], [254, 178, 76],
    [253, 141, 60], [252, 78, 42], [227, 26, 28], [177, 0, 38],
  ];
  const t = Math.max(0, Math.min(1, Number(speedMs || 0) / 20));
  const scaled = t * (stops.length - 1);
  const lower = Math.floor(scaled);
  const upper = Math.min(stops.length - 1, lower + 1);
  const mix = scaled - lower;
  const rgb = stops[lower].map((channel, index) => Math.round(channel + (stops[upper][index] - channel) * mix));
  return [...rgb, 118];
};

// Reject malformed polygons that bridge the antimeridian. This is a defensive
// client-side guard for cached forecast tiles; freshly published Indo-Pacific
// output stays in the source 0–360° longitude domain and does not need it.
const geometryLongitudeSpan = (geometry) => {
  const lons = [];
  const visit = value => {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number') { lons.push(value[0]); return; }
    value.forEach(visit);
  };
  visit(geometry?.coordinates);
  return lons.length ? Math.max(...lons) - Math.min(...lons) : 0;
};

const isRenderableForecastWindFeature = feature => geometryLongitudeSpan(feature?.geometry) < 300;

const longitudeDistance = (a, b) => Math.abs((((a - b) + 540) % 360) - 180);

const windViewportExtent = (viewState) => {
  const zoom = Math.max(0, Number(viewState?.zoom) || 0);
  // Larger than the visible WebMercator extent so wide screens, pitch, and
  // map-edge panning never expose an empty vector band before the next load.
  return {
    halfLon: Math.min(180, 640 / (2 ** zoom)),
    halfLat: Math.min(85, 320 / (2 ** zoom)),
  };
};

// Approximate a WebMercator viewport with a deliberate guard band. This keeps
// vectors available just beyond the screen edge while avoiding work on the
// rest of the Pacific. The bounded view is refreshed after interactions settle.
const sourceIsNearViewport = (source, viewState) => {
  if (!viewState || !Number.isFinite(viewState.longitude) || !Number.isFinite(viewState.latitude)) return true;
  const { halfLon, halfLat } = windViewportExtent(viewState);
  const [lon, lat] = source.position;
  return longitudeDistance(lon, viewState.longitude) <= halfLon && Math.abs(lat - viewState.latitude) <= halfLat;
};

const viewportBbox = (viewState) => {
  if (!viewState || !Number.isFinite(viewState.longitude) || !Number.isFinite(viewState.latitude)) return null;
  const { halfLon, halfLat } = windViewportExtent(viewState);
  const minX = viewState.longitude - halfLon;
  const maxX = viewState.longitude + halfLon;
  // A dateline-spanning request cannot be represented by one FlatGeobuf rect.
  // In that uncommon broad view we retain the normal whole-file fallback.
  if (minX < -180 || maxX > 360) return null;
  return { minX, minY: Math.max(-85, viewState.latitude - halfLat), maxX, maxY: Math.min(85, viewState.latitude + halfLat) };
};

// Keep the Windy-style effect usable on integrated GPUs and older laptops.
// These limits only reduce visual samples: the underlying GFS/CFS field and
// interpolation remain unchanged. High-end devices retain the full density.
const getWindParticleQuality = () => {
  if (typeof navigator === 'undefined') {
    return { basinLimit: 3600, localLimit: 1800, trailSampleEvery: 1, maxTrailPoints: 18 };
  }
  const cores = Number(navigator.hardwareConcurrency) || 4;
  const memory = Number(navigator.deviceMemory);
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion || cores <= 2 || (Number.isFinite(memory) && memory <= 2)) {
    return { basinLimit: 1400, localLimit: 900, trailSampleEvery: 1, maxTrailPoints: 14 };
  }
  if (cores <= 4 || (Number.isFinite(memory) && memory <= 4)) {
    return { basinLimit: 3200, localLimit: 1800, trailSampleEvery: 1, maxTrailPoints: 18 };
  }
  return { basinLimit: 6400, localLimit: Number.MAX_SAFE_INTEGER, trailSampleEvery: 1, maxTrailPoints: 18 };
};

const featureCentroid = (feature) => {
  const geometry = feature?.geometry;
  if (!geometry) return null;
  if (geometry.type === 'Point') return geometry.coordinates;
  const ring = geometry.coordinates?.[0];
  if (!ring?.length) return null;
  const total = ring.reduce((acc, point) => [acc[0] + point[0], acc[1] + point[1]], [0, 0]);
  return [total[0] / ring.length, total[1] / ring.length];
};

const windParticlesFromFeatures = (data, limit = 1800) => {
  const features = data?.features || [];
  const stride = Math.max(1, Math.ceil(features.length / limit));
  return features.filter((feature, index) => index % stride === 0 && isRenderableForecastWindFeature(feature)).map((feature) => {
    const p = feature.properties || {};
    const u = Number(p.u10 ?? p.u ?? p.ugrd ?? p.UGRD ?? p.u_wind);
    const v = Number(p.v10 ?? p.v ?? p.vgrd ?? p.VGRD ?? p.v_wind);
    const magnitude = Number(p.wind_speed ?? p.wind ?? p.value ?? p.speed ?? Math.hypot(u || 0, v || 0));
    // EEZ tiles are clipped geometrically before publishing.  A clipped cell's
    // visual centroid no longer coincides with the GFS grid node, which breaks
    // the rectilinear field used by the particle interpolator.  Keep advection
    // anchored to the source grid coordinates carried with every feature.
    const sourceLon = Number(p.lon ?? p.longitude ?? p.lng);
    const sourceLat = Number(p.lat ?? p.latitude);
    const position = Number.isFinite(sourceLon) && Number.isFinite(sourceLat)
      ? [sourceLon, sourceLat]
      : featureCentroid(feature);
    if (!position || !Number.isFinite(magnitude)) return null;
    // Meteorological bearing: direction the air is travelling toward.
    const angle = Number.isFinite(u) && Number.isFinite(v)
      ? (Math.atan2(u, v) * 180 / Math.PI + 360) % 360
      : Number(p.wind_direction ?? p.direction ?? p.dp ?? 0);
    if (!Number.isFinite(u) || !Number.isFinite(v)) return null;
    return { position, angle, magnitude: Math.max(0.15, magnitude / 12), u, v };
  }).filter(Boolean);
};

// Low-latency directional vectors: one static shaft + chevron per sampled
// source-grid vector. These do not allocate animation frames or particle trails.
const staticWindVectors = (sources, isLocalWindView, baseLengthOverride) => {
  const baseLength = baseLengthOverride ?? (isLocalWindView ? 0.11 : 0.42);
  // Unlike the animated mode, this has no frame-loop or trail allocation.
  // Preserve every valid source-grid vector for a complete NOAA-style field.
  return sources.map((source) => {
    const [lon, lat] = source.position;
    const speed = Math.hypot(source.u || 0, source.v || 0);
    const length = baseLength * Math.max(0.65, Math.min(1.6, 0.65 + speed / 18));
    const radians = source.angle * Math.PI / 180;
    const dx = Math.sin(radians) * length / Math.max(0.25, Math.cos(lat * Math.PI / 180));
    const dy = Math.cos(radians) * length;
    const head = [lon + dx, lat + dy];
    const wingLength = length * 0.34;
    const left = [
      head[0] + Math.sin(radians + Math.PI * 0.78) * wingLength / Math.max(0.25, Math.cos(lat * Math.PI / 180)),
      head[1] + Math.cos(radians + Math.PI * 0.78) * wingLength,
    ];
    const right = [
      head[0] + Math.sin(radians - Math.PI * 0.78) * wingLength / Math.max(0.25, Math.cos(lat * Math.PI / 180)),
      head[1] + Math.cos(radians - Math.PI * 0.78) * wingLength,
    ];
    return { path: [[lon, lat], head, left, head, right], speed };
  });
};

const MS_TO_KNOTS = 1.943844;

const offsetFromBearing = (origin, bearingRadians, distance) => {
  const [lon, lat] = origin;
  const cosLat = Math.max(0.25, Math.cos(lat * Math.PI / 180));
  return [
    lon + Math.sin(bearingRadians) * distance / cosLat,
    lat + Math.cos(bearingRadians) * distance,
  ];
};

// Standard meteorological / NOAA wind-barb geometry.  The shaft points to the
// direction FROM which the air originates.  Barbs remain on the left side of
// the shaft; a short barb = 5 kt, long barb = 10 kt, and a filled pennant =
// 50 kt.  Speeds are rounded to the nearest five knots, with 0–2 kt rendered
// as the conventional calm-wind circle.
const staticWindBarbs = (sources, isLocalWindView) => {
  const shaftLength = isLocalWindView ? 0.085 : 0.34;
  const circleRadius = shaftLength * 0.22;
  const strokes = [];
  const pennants = [];

  sources.forEach((source) => {
    const speedKnots = Math.hypot(source.u || 0, source.v || 0) * MS_TO_KNOTS;
    const roundedKnots = Math.max(0, Math.round(speedKnots / 5) * 5);
    const anchor = source.position;

    if (roundedKnots < 5) {
      const ring = Array.from({ length: 17 }, (_, index) => (
        offsetFromBearing(anchor, index * Math.PI * 2 / 16, circleRadius)
      ));
      strokes.push({ path: ring, calm: true });
      return;
    }

    // U/V point toward the moving air.  Meteorological barbs point from the
    // opposite bearing—the source direction of the wind.
    const fromBearing = Math.atan2(-(source.u || 0), -(source.v || 0));
    const leftBearing = fromBearing - Math.PI / 2;
    const shaftEnd = offsetFromBearing(anchor, fromBearing, shaftLength);
    strokes.push({ path: [anchor, shaftEnd], calm: false });

    let remaining = roundedKnots;
    const pennantCount = Math.floor(remaining / 50);
    remaining -= pennantCount * 50;
    const fullBarbCount = Math.floor(remaining / 10);
    remaining -= fullBarbCount * 10;
    const hasHalfBarb = remaining >= 5;
    let cursor = shaftLength;
    const pennantStep = shaftLength * 0.22;
    const featherStep = shaftLength * 0.14;

    for (let index = 0; index < pennantCount; index += 1) {
      const tip = offsetFromBearing(anchor, fromBearing, cursor);
      const base = offsetFromBearing(anchor, fromBearing, cursor - pennantStep);
      const outer = offsetFromBearing(base, leftBearing, shaftLength * 0.38);
      pennants.push({ polygon: [tip, base, outer] });
      cursor -= pennantStep;
    }

    const appendBarb = (length) => {
      const base = offsetFromBearing(anchor, fromBearing, cursor);
      // Feather slopes back toward the station and left of the shaft, matching
      // the convention used by NOAA/NWS chart products.
      const outer = offsetFromBearing(
        offsetFromBearing(base, fromBearing + Math.PI, shaftLength * 0.07),
        leftBearing,
        length,
      );
      strokes.push({ path: [base, outer], calm: false });
      cursor -= featherStep;
    };

    for (let index = 0; index < fullBarbCount; index += 1) appendBarb(shaftLength * 0.34);
    if (hasHalfBarb) appendBarb(shaftLength * 0.19);
  });

  return { strokes, pennants };
};

const bracket = (values, value) => {
  if (value < values[0] || value > values[values.length - 1]) return null;
  let low = 0; let high = values.length - 1;
  while (high - low > 1) {
    const mid = (low + high) >> 1;
    if (values[mid] <= value) low = mid; else high = mid;
  }
  return [values[low], values[high]];
};

const makeVectorField = (sources) => {
  if (!sources.length) return null;
  const lons = [...new Set(sources.map(s => s.position[0]))].sort((a, b) => a - b);
  const lats = [...new Set(sources.map(s => s.position[1]))].sort((a, b) => a - b);
  const key = (lon, lat) => `${lon.toFixed(6)}:${lat.toFixed(6)}`;
  const values = new Map(sources.map(source => [key(source.position[0], source.position[1]), source]));
  const nearest = (lon, lat) => values.get(key(
    lons.reduce((best, x) => Math.abs(x - lon) < Math.abs(best - lon) ? x : best, lons[0]),
    lats.reduce((best, y) => Math.abs(y - lat) < Math.abs(best - lat) ? y : best, lats[0]),
  ));
  return {
    bounds: { minLon: lons[0], maxLon: lons[lons.length - 1], minLat: lats[0], maxLat: lats[lats.length - 1] },
    sources,
    sample(lon, lat) {
      const xb = bracket(lons, lon); const yb = bracket(lats, lat);
      if (!xb || !yb) return null;
      const [x0, x1] = xb; const [y0, y1] = yb;
      const corners = [values.get(key(x0, y0)), values.get(key(x1, y0)), values.get(key(x0, y1)), values.get(key(x1, y1))];
      if (corners.some(v => !v) || x0 === x1 || y0 === y1) return nearest(lon, lat) || null;
      const tx = (lon - x0) / (x1 - x0); const ty = (lat - y0) / (y1 - y0);
      const blend = prop => (
        corners[0][prop] * (1 - tx) * (1 - ty) + corners[1][prop] * tx * (1 - ty)
        + corners[2][prop] * (1 - tx) * ty + corners[3][prop] * tx * ty
      );
      return { u: blend('u'), v: blend('v'), magnitude: blend('magnitude') };
    },
  };
};

// Helper function moved outside component scope
const findClosestKey = (dict, target) => {
  const keys = Object.keys(dict).map(Number); // Convert keys to numbers

  // Find the closest key
  let closestKey = keys[0];
  let minDiff = Math.abs(target - keys[0]);

  for (let i = 1; i < keys.length; i++) {
    const diff = Math.abs(target - keys[i]);
    if (diff < minDiff || (diff === minDiff && keys[i] < closestKey)) {
      closestKey = keys[i];
      minDiff = diff;
    }
  }
  return closestKey;
};

// Helper function to parse ACCESS-S WKT format to standard GeoJSON
const parseAccessSData = (data) => {
  if (!data || !data.geometry_x) return null;
  const features = [];
  const keys = Object.keys(data.time || {});
  
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const wkt = data.geometry_x[key];
    if (!wkt) continue;
    
    // Convert WKT POLYGON ((...)) to Coordinate Arrays
    const coordString = wkt.replace(/^POLYGON\s*\(\(/, "").replace(/\)\)$/, "");
    const pairs = coordString.split(",");
    const coordinates = pairs.map(p => {
      const [lon, lat] = p.trim().split(/\s+/);
      return [parseFloat(lon), parseFloat(lat)];
    });

    features.push({
      type: "Feature",
      properties: { value: data.rain[key], rain: data.rain[key], lat: data.lat[key], lon: data.lon[key] },
      geometry: { type: "Polygon", coordinates: [coordinates] }
    });
  }
  
  return { type: "FeatureCollection", features };
};

// --- Custom Windy-like Particle Layer ---
class WindyLayer extends Layer {
  getShaders() {
    return {
      vs: `
        #define SHADER_NAME windy-layer-vs
        attribute vec3 positions;
        attribute vec3 instancePositions;
        attribute float instanceAngles;
        attribute float instanceMagnitudes;
        attribute float instancePhases;
        
        uniform float uTime;
        uniform float uWidth;
        
        varying vec2 vTexCoord;
        varying float vMagnitude;
        varying float vPhase;
        
        void main(void) {
          vTexCoord = positions.xy + 0.5; 
          vMagnitude = instanceMagnitudes;
          vPhase = instancePhases;
          
          // Convert Meteorological Angle (0=N, 90=E) to Radians (Standard Math: 0=E, CCW)
          // angleRad = (90 - angle) * PI / 180
          float angleRad = radians(90.0 - instanceAngles);
          
          // Wave height adds modest visual emphasis, not map-spanning streaks.
          // The prior multiplier made normal 2–3 m seas read as 75 px lines.
          float len = 10.0 + clamp(instanceMagnitudes, 0.0, 4.0) * 5.0;
          
          // Rotate and Scale
          float c = cos(angleRad);
          float s = sin(angleRad);
          mat2 rot = mat2(c, s, -s, c);
          
          vec2 pos = positions.xy;
          pos.x *= len; // Stretch length
          pos.y *= uWidth; // Width
          
          pos = rot * pos;
          
          vec3 center = instancePositions;
          vec3 offset = vec3(project_pixel_size(pos.x), project_pixel_size(pos.y), 0.0);
          
          vec3 projected = project_position(center);
          gl_Position = project_common_position_to_clipspace(vec4(projected + offset, 1.0));
        }
      `,
      fs: `
        #define SHADER_NAME windy-layer-fs
        precision highp float;
        
        varying vec2 vTexCoord;
        varying float vMagnitude;
        varying float vPhase;
        
        uniform float uTime;
        uniform vec3 uColor;
        
        void main(void) {
          // Animate a trail moving from 0 to 1 along the length
          float speed = 0.5 + (vMagnitude * 0.1); 
          // Keep wave segments independently phased. A shared phase makes an
          // entire dense field blink out at once while the map is zooming.
          float t = fract(uTime * speed + vPhase);
          
          // Distance from head of the trail
          float dist = vTexCoord.x - t;
          if (dist > 0.0) dist -= 1.0;
          
          // Create fading trail effect
          float alpha = smoothstep(-0.6, 0.0, dist) * smoothstep(0.0, -0.05, dist);
          
          // Soften edges vertically
          alpha *= smoothstep(0.0, 0.2, vTexCoord.y) * (1.0 - smoothstep(0.8, 1.0, vTexCoord.y));
          
          if (alpha < 0.01) discard;
          gl_FragColor = vec4(uColor, alpha);
        }
      `,
      modules: [project32, picking]
    };
  }

  initializeState() {
    const attributeManager = this.getAttributeManager();
    attributeManager.addInstanced({
      instancePositions: { size: 3, type: GL.DOUBLE, fp64: this.use64bitPositions(), accessor: 'getPosition' },
      instanceAngles: { size: 1, accessor: 'getAngle' },
      instanceMagnitudes: { size: 1, accessor: 'getMagnitude' },
      instancePhases: { size: 1, accessor: 'getPhase' },
      instanceColors: { size: 4, type: GL.UNSIGNED_BYTE, normalized: true, accessor: 'getColor' }
    });
  }

  updateState({ changeFlags }) {
    // A custom deck.gl Layer has no model until we explicitly create one.
    // Checking only extensionsChanged left the wave layer without geometry on
    // ordinary map zoom updates, which is why the animated field vanished.
    if (!this.state.model || changeFlags.extensionsChanged) {
      const { gl } = this.context;
      if (this.state.model) this.state.model.delete();
      const positions = new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, -0.5, 0.5, 0, 0.5, 0.5, 0]);
      this.setState({
        model: new Model(gl, { id: this.props.id, vs: this.getShaders().vs, fs: this.getShaders().fs, modules: this.getShaders().modules, geometry: new Geometry({ drawMode: GL.TRIANGLE_STRIP, attributes: { positions: { size: 3, value: positions } } }), vertexCount: 4, isInstanced: true })
      });
    }
  }

  draw({ uniforms }) {
    const color = this.props.color || [255, 255, 255];
    this.state.model.setUniforms(uniforms).setUniforms({
      uTime: this.props.time,
      uWidth: this.props.width || 1.0,
      uColor: color.map(channel => channel / 255),
    }).draw();
  }
}
WindyLayer.layerName = 'WindyLayer';
WindyLayer.defaultProps = { getPosition: { type: 'accessor', value: x => x.position }, getAngle: { type: 'accessor', value: 0 }, getMagnitude: { type: 'accessor', value: 1 }, getPhase: { type: 'accessor', value: () => 0 }, getColor: { type: 'accessor', value: [255, 255, 255, 255] }, color: { type: 'array', value: [255, 255, 255], compare: true }, time: { type: 'number', value: 0, compare: true }, width: { type: 'number', value: 1, compare: true } };

const waveVectorDensityTier = (zoom, isRmi) => {
  const baseZoom = isRmi ? 4.7 : 6.5;
  // Country overview begins with one in four source vectors. Each additional
  // zoom level reveals roughly another quarter, reaching every sample after
  // three levels. Integer tiers prevent a continuous zoom gesture from
  // repeatedly replacing the vector field with transient partial arrays.
  return Math.min(4, Math.max(1, 1 + Math.floor(Number(zoom ?? baseZoom) - baseZoom)));
};

const waveVectorsForZoom = (samples, tier) => {
  const sampleList = samples || [];
  if (tier >= 4 || sampleList.length < 4) return sampleList;

  // Source records are latitude-major, so `index % n` makes a dense lower
  // band and a sparse upper band. Work in the source grid's row/column space
  // instead: 25% is an evenly distributed checkerboard lattice, not the
  // first quarter of the feed.
  const latitudes = [...new Set(sampleList.map(item => Number(item.latitude)).filter(Number.isFinite))].sort((a, b) => a - b);
  const longitudes = [...new Set(sampleList.map(item => Number(item.longitude)).filter(Number.isFinite))].sort((a, b) => a - b);
  const latIndex = new Map(latitudes.map((value, index) => [value.toFixed(6), index]));
  const lonIndex = new Map(longitudes.map((value, index) => [value.toFixed(6), index]));
  const count = Math.max(1, tier);
  const acceptedPhases = count === 1 ? [0] : count === 2 ? [0, 3] : [0, 1, 2];

  return sampleList.filter((sample) => {
    const row = latIndex.get(Number(sample.latitude).toFixed(6));
    const column = lonIndex.get(Number(sample.longitude).toFixed(6));
    if (row == null || column == null) return false;
    return acceptedPhases.includes((row % 2) * 2 + (column % 2));
  });
};

const waveStaticVectorSources = (samples) => (samples || []).map((sample) => {
  const lon = Number(sample.longitude);
  const lat = Number(sample.latitude);
  const angle = Number(sample.direction_to_deg ?? sample.dp);
  const waveHeight = Number(sample.wave_height_m ?? sample.magnitude ?? 1);
  if (![lon, lat, angle].every(Number.isFinite)) return null;
  const radians = angle * Math.PI / 180;
  // Match the established GFS/CFS vector geometry: source vectors point in
  // their direction of travel and receive one shaft plus a symmetric head.
  return {
    position: [lon, lat],
    angle,
    u: Math.sin(radians) * waveHeight,
    v: Math.cos(radians) * waveHeight,
  };
}).filter(Boolean);

export const useMapLayers = ({ filePath, onClick, showWindParticles = true, windRenderMode = 'particles', waveVectorMode = 'animated', mapZoom = 2, mapViewState = null, retainPreviousLayers = true }) => {
  const mapViewport = useMemo(() => ({
    longitude: mapViewState?.longitude,
    latitude: mapViewState?.latitude,
    zoom: mapViewState?.zoom,
  }), [mapViewState?.longitude, mapViewState?.latitude, mapViewState?.zoom]);
  const [settledViewState, setSettledViewState] = useState(mapViewport);
  const [pageVisible, setPageVisible] = useState(() => typeof document === 'undefined' || !document.hidden);
  const windBbox = viewportBbox(settledViewState);
  const windBboxKey = windBbox ? Object.values(windBbox).map(value => value.toFixed(2)).join(',') : 'all';

  useEffect(() => {
    const timer = setTimeout(() => setSettledViewState(mapViewport), 180);
    return () => clearTimeout(timer);
  }, [mapViewport]);

  useEffect(() => {
    const updateVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', updateVisibility);
    return () => document.removeEventListener('visibilitychange', updateVisibility);
  }, []);

  const { data: mapDataSource, isLoading } = useQueries({
    queries: (Array.isArray(filePath) ? filePath : []).map((path) => {
      const isStatic =
        path.includes('EEZ') ||
        path.includes('palau') ||
        path.includes('station') ||
        path.includes('chlor');

      const isForecastWind = /\/forecasts\/(?:gfs|cfs)\/wind\//.test(path);
      // National CRW clips are small enough to load as a complete layer. Only
      // the (backend-download-only) native Pacific product needs viewport
      // queries to avoid parsing the entire basin in a browser.
      const isNativePacificCrw = path.includes('/coral-reef-watch/pacific/native/');
      return {
        queryKey: ['mapLayer', path, (isForecastWind || isNativePacificCrw) ? windBboxKey : 'full'],
        queryFn: () => getJSON(path, (isForecastWind || isNativePacificCrw) ? { bbox: windBbox } : undefined),
        staleTime: Infinity,
        // TanStack Query v5 renamed cacheTime to gcTime. The old option was
        // silently ignored, leaving every timeline frame resident much longer
        // than intended and eventually exhausting browser memory.
        gcTime: isStatic ? Infinity : 1000 * 15,
        refetchOnWindowFocus: false,
      };
    }),
    combine: (results) => {
      const allSuccess = results.every((r) => r.isSuccess && r.data);
      // Consider loading if hard loading (isLoading) or fetching without data (isFetching && !data)
      const anyLoading = results.some((r) => r.isLoading || (r.isFetching && !r.data));
      return {
        data: allSuccess ? results.map((r) => r.data) : null,
        isLoading: anyLoading
      };
    },
  });

  // Keep track of the last successfully generated layers to prevent flashing during loads
  const lastValidLayers = useRef([]);
  const lastPathKey = useRef('');

  // Drop stale layers when the map product family changes (e.g. Palau → Indo-Pacific),
  // otherwise the old extent stays on screen until a later slider tick.
  useEffect(() => {
    const key = Array.isArray(filePath) ? (filePath[0] || '') : '';
    const folderKey = key.replace(/\/[^/]+$/, '');
    if (lastPathKey.current && lastPathKey.current !== folderKey) {
      lastValidLayers.current = [];
    }
    lastPathKey.current = folderKey;
  }, [filePath]);

  // Animation Loop for Windy Layer
  const [time, setTime] = useState(0);
  const requestRef = useRef();
  const [flowParticles, setFlowParticles] = useState([]);

  const vectorSources = useMemo(() => {
    if (!mapDataSource || !Array.isArray(filePath)) return [];
    return filePath.reduce((sources, path, index) => (
      /\/forecasts\/(?:gfs|cfs)\/wind\//.test(path)
        // At EEZ scale, preserve every valid grid cell so the display has one
        // advected particle per local vector. Basin tiles remain capped.
        ? sources.concat(windParticlesFromFeatures(
          mapDataSource[index],
          windRenderMode === 'vectors' || windRenderMode === 'barbs'
            ? Number.MAX_SAFE_INTEGER
            : (/\/wind\/(?:palau|rmi)\//.test(path) ? Number.MAX_SAFE_INTEGER : 14000),
        ))
        : sources
    ), []);
  }, [mapDataSource, filePath, windRenderMode]);
  const visibleVectorSources = useMemo(
    () => vectorSources.filter(source => sourceIsNearViewport(source, settledViewState)),
    [vectorSources, settledViewState],
  );
  const vectorField = useMemo(() => makeVectorField(visibleVectorSources), [visibleVectorSources]);
  const particleQuality = useMemo(getWindParticleQuality, []);
  const isLocalWindView = useMemo(() => (Array.isArray(filePath) && filePath.some(
    path => /\/forecasts\/(?:gfs|cfs)\/wind\/(?:palau|rmi)\//.test(path),
  )), [filePath]);
  const windBarbGeometry = useMemo(
    () => staticWindBarbs(visibleVectorSources, isLocalWindView),
    [visibleVectorSources, isLocalWindView],
  );

  // True particle advection: particles carry the local U/V vector, advance in
  // geographic space every frame, fade with age, then respawn on the field.
  useEffect(() => {
    if (!vectorField || !showWindParticles || windRenderMode !== 'particles' || !pageVisible) {
      setFlowParticles([]);
      return undefined;
    }
    const spawn = () => {
      const source = vectorField.sources[Math.floor(Math.random() * vectorField.sources.length)];
      // Staggered but bounded lifetimes avoid respawn flashes without leaving
      // a particle visible long enough to form an overgrown streamline.
      return {
        position: [...source.position], trail: [[...source.position]], trailFrame: 0,
        age: Math.random() * 3, life: 8 + Math.random() * 4, magnitude: source.magnitude,
      };
    };
    // Start at twice the prior density, then increase smoothly as the user
    // zooms in. The field-source cap guarantees we never exceed grid cells.
    const zoomDensity = Math.min(2, 1 + Math.max(0, Number(mapZoom || 2) - 2) * 0.25);
    const particleCount = isLocalWindView
      ? Math.min(vectorField.sources.length, particleQuality.localLimit)
      : Math.min(Math.floor(particleQuality.basinLimit * zoomDensity), vectorField.sources.length);
    let particles = Array.from({ length: particleCount }, spawn);
    let frame = 0;
    let lastPaint = performance.now();
    const animateFlow = (now) => {
      // 30fps state commits retain smooth trails without rebuilding deck.gl at
      // every display refresh; vector integration still uses elapsed time.
      if (now - lastPaint < 33) { frame = requestAnimationFrame(animateFlow); return; }
      const dt = Math.min(0.08, (now - lastPaint) / 1000);
      lastPaint = now;
      particles = particles.map((particle) => {
        const age = particle.age + dt;
        const vector = vectorField.sample(particle.position[0], particle.position[1]);
        if (age >= particle.life || !vector) return spawn();
        const lat = particle.position[1];
        // Visual advection is intentionally accelerated relative to physical
        // metres/second: at a whole-Pacific zoom, physical 10 m winds move
        // less than a pixel per second and read as stationary dots.
        // Keep local EEZ fields visibly advected.  Dividing this by four made
        // the 0.25°/CFS cells read as static dots at normal country zooms.
        // Trail history remains tightly capped below, so this never recreates
        // the former map-spanning, persistent tails.
        const travelScale = 0.045;
        const lonStep = (vector.u * dt * travelScale) / Math.max(0.25, Math.cos(lat * Math.PI / 180));
        const latStep = vector.v * dt * travelScale;
        const position = [particle.position[0] + lonStep, lat + latStep];
        // Hold only a short, sampled history. This is a tail-duration cap,
        // independent of the particle's lifetime, so a calm particle can
        // never accumulate a map-spanning trail.
        const trailFrame = particle.trailFrame + 1;
        const trail = trailFrame % particleQuality.trailSampleEvery === 0
          ? [...particle.trail, position].slice(-particleQuality.maxTrailPoints)
          : particle.trail;
        return { ...particle, age, position, trail, trailFrame, magnitude: vector.magnitude };
      });
      setFlowParticles(particles);
      frame = requestAnimationFrame(animateFlow);
    };
    frame = requestAnimationFrame(animateFlow);
    return () => cancelAnimationFrame(frame);
  }, [vectorField, showWindParticles, windRenderMode, mapZoom, isLocalWindView, particleQuality, pageVisible]);
  
  const animate = useCallback(() => {
    setTime(t => (t + 0.02) % 1000.0); // Continuous time increment
    requestRef.current = requestAnimationFrame(animate);
  }, []);
  
  // Run animation loop if we have layers that need it (windy arrows, station pulses)
  const needsAnimation = useMemo(() => {
    if (!pageVisible) return false;
    const paths = Array.isArray(filePath) ? filePath : [];
    return paths.some(path =>
        ((path.includes("arrow") || path.includes("Arrow")) &&
          !(path.includes('/wave-hindcast/') && waveVectorMode === 'arrows')) ||
        (showWindParticles && windRenderMode === 'particles' && /\/forecasts\/(?:gfs|cfs)\/wind\//.test(path)) ||
        path.includes("icon") || path.includes("Icon") || path.includes("station")
    );
  }, [filePath, showWindParticles, windRenderMode, waveVectorMode, pageVisible]);

  useEffect(() => {
    if (needsAnimation) {
      requestRef.current = requestAnimationFrame(animate);
    }
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [animate, needsAnimation]);

  const layers = useMemo(() => {
    if (!filePath || !Array.isArray(filePath) || filePath.length === 0 || !mapDataSource) {
      // If loading (no mapDataSource) but we have history, return history to prevent flash
      if (retainPreviousLayers && lastValidLayers.current.length > 0) {
        return lastValidLayers.current;
      }
      return [];
    }

    const deckLayers = [];
    const arrayLength = filePath.length;

    try {
        // Ensure data length matches path length to avoid mismatches during rapid switching
        if (Array.isArray(mapDataSource) && mapDataSource.length === arrayLength) {
            for (let i = 0; i < arrayLength; i++) {
                 const currentData = mapDataSource[i]; // Data for this path
                 const path = filePath[i];
                 const clickHandler = Array.isArray(onClick) && onClick.length > i ? onClick[i] : null;
                
                if (path.includes('/convergence-zone/')) {
                  const isClimatology = path.includes('/climatology/');
                  const lineColor = isClimatology ? [74, 222, 128, 255] : [244, 114, 182, 255];
                  const layerKey = path.replace(/[^a-z0-9]+/gi, '-');
                  // A slim dark casing keeps both solid isotherms legible on
                  // light SST cells and on the red/blue Ekman palette without
                  // introducing an areal fill.
                  deckLayers.push(new GeoJsonLayer({
                    id: `convergence-zone-casing-${layerKey}`,
                    data: currentData,
                    filled: false,
                    stroked: true,
                    getLineColor: [7, 15, 29, 205],
                    getLineWidth: isClimatology ? 5 : 5.5,
                    lineWidthUnits: 'pixels',
                    lineWidthMinPixels: isClimatology ? 4 : 4.5,
                    lineWidthMaxPixels: 7,
                    jointRounded: true,
                    capRounded: true,
                    pickable: false,
                    wrapLongitude: false,
                    parameters: { depthTest: false },
                  }));
                  deckLayers.push(new GeoJsonLayer({
                    id: `convergence-zone-line-${layerKey}`,
                    data: currentData,
                    filled: false,
                    stroked: true,
                    getLineColor: lineColor,
                    getLineWidth: isClimatology ? 2.3 : 2.8,
                    lineWidthUnits: 'pixels',
                    lineWidthMinPixels: isClimatology ? 2 : 2.5,
                    lineWidthMaxPixels: 4,
                    jointRounded: true,
                    capRounded: true,
                    pickable: false,
                    wrapLongitude: false,
                    parameters: { depthTest: false },
                  }));
                } else if (path.includes("trend_rsl.json")){
                  var trendRadius = currentData[filePath[0].substring(28, 38)]["trend_mag_rsl"]*100
                  const closestColor = cmapMako[findClosestKey(cmapMako, trendRadius)];
                  
                  deckLayers.push(new ScatterplotLayer({
                    id: 'scatterplot-layer',
                    stroke: true,
                    data:[{position: [134.463, 7.33]}],
                    getPosition: d => d.position,
                    getRadius: trendRadius*100*2,
                    getFillColor: closestColor,
                    getLineColor: [0, 0, 0],
                    getLineWidth: 10,
                    radiusScale: 6,
                    pickable: true
                    }))
                }
                if(path.includes("arrow")||path.includes("Arrow")){
                     const isWaveArrow = path.includes('/wave-hindcast/');
                     // Use the debounced map state. It is stable after a map
                     // gesture ends, which keeps this layer live during zoom
                     // rather than rebuilding it for every wheel tick.
                     const waveZoom = settledViewState?.zoom ?? mapZoom;
                     const waveDensityTier = isWaveArrow
                       ? waveVectorDensityTier(waveZoom, path.includes('/rmi/'))
                       : null;
                     const waveVectorData = isWaveArrow
                       ? waveVectorsForZoom(currentData, waveDensityTier)
                       : currentData;
                     const layerKey = path.replace(/[^a-z0-9]+/gi, '-');
                     if (isWaveArrow && waveVectorMode === 'arrows') {
                       deckLayers.push(new PathLayer({
                         id: `wave-static-arrows-${layerKey}-density-${waveDensityTier}`,
                         data: staticWindVectors(waveStaticVectorSources(waveVectorData), true, 0.075),
                         getPath: data => data.path,
                         getColor: [66, 178, 255, 220],
                         getWidth: 1.7,
                         widthUnits: 'pixels',
                         widthMinPixels: 1.5,
                         widthMaxPixels: 2.5,
                         pickable: false,
                       }));
                     } else {
                       // Scope is intentionally part of the ID. Otherwise
                       // deck.gl may retain Palau's animated model when the
                       // user changes the WaveWatch view to Marshall Islands.
                       deckLayers.push(new WindyLayer({
                        id: `windy-arrow-visual-${layerKey}-density-${waveDensityTier}`,
                        data: waveVectorData,
                        getPosition: data => [data.longitude, data.latitude],
                        getAngle: data => data.dp,
                        getMagnitude: data => data.magnitude || 1.0,
                        getPhase: data => {
                          const lon = Number(data.longitude) || 0;
                          const lat = Number(data.latitude) || 0;
                          return ((Math.sin(lon * 12.9898 + lat * 78.233) * 43758.5453) % 1 + 1) % 1;
                        },
                        // Waves should read as a gentle propagation pattern,
                        // not the faster wind-field cadence.
                        time: time * 0.3,
                        width: 2.5,
                        color: [66, 178, 255],
                        pickable: false
                      }));
                     }

                     // 2. Invisible Hitbox Layer (For Clicking)
                     deckLayers.push(new ScatterplotLayer({
                         id: `windy-arrow-hitbox-${layerKey}`,
                         data: waveVectorData,
                         getPosition: data => [data.longitude, data.latitude],
                         getRadius: 10,
                         radiusMinPixels: 15, // Gives a generous 15px radius for easier clicking
                         getFillColor: [0, 0, 0, 0], // Fully transparent
                         pickable: true,
                         onClick: clickHandler
                     }));
                } else if (path.includes("icon") || path.includes("Icon") || path.includes("station")) {
                    const stationScope = new URLSearchParams(String(path).split('?')[1] || '').get('scope');
                    const stationData = stationScope && Array.isArray(currentData)
                      ? currentData.filter(station => (
                          stationScope === 'rmi'
                            ? station.country === 'Marshall Islands'
                            : station.country === 'Palau'
                        ))
                      : currentData;
                    // Pulsing Halo Layer (underneath the icon)
                    deckLayers.push(new ScatterplotLayer({
                        id: `station-halo-${i}`,
                        data: stationData,
                        getPosition: d => [d.longitude, d.latitude],
                        getFillColor: [34, 211, 238, 80], // Translucent cyan
                        // Animate the radius using the time state
                        getRadius: 14 + Math.sin(time * 1.5) * 4,
                        radiusUnits: 'pixels',
                        stroked: false,
                        pickable: false,
                    }));

                    // New Icon: Static Outer Ring
                    deckLayers.push(new ScatterplotLayer({
                        id: `station-ring-${i}`,
                        data: stationData,
                        getPosition: d => [d.longitude, d.latitude],
                        stroked: true,
                        getLineWidth: 2,
                        lineWidthUnits: 'pixels',
                        getLineColor: [255, 255, 255, 200],
                        getFillColor: [0, 0, 0, 0], // transparent fill
                        getRadius: 12,
                        radiusUnits: 'pixels',
                        pickable: false,
                    }));

                    // New Icon: Inner Dot (this is the main clickable element)
                    deckLayers.push(new ScatterplotLayer({
                        id: `station-dot-${i}`,
                        data: stationData,
                        getPosition: d => [d.longitude, d.latitude],
                        stroked: false,
                        getFillColor: [34, 211, 238], // Solid cyan
                        getRadius: 5,
                        radiusUnits: 'pixels',
                        pickable: true,
                        onClick: clickHandler,
                    }));

                    // New Label Layer
                    deckLayers.push(new TextLayer({
                        id: `station-label-${i}`, data: currentData, getPosition: d => [d.longitude, d.latitude], getText: d => d.name,
                        getColor: [255, 255, 255, 255], getSize: 12, getPixelOffset: [0, -35], getAngle: 0, getTextAnchor: 'middle',
                        getAlignmentBaseline: 'center', fontFamily: 'sans-serif', fontWeight: 'bold', background: true,
                        getBackgroundColor: [15, 23, 42, 200], getBorderColor: [34, 211, 238, 255], getBorderWidth: 1,
                        backgroundPadding: [6, 3], pickable: false,
                    }));
                } else if (path.includes('/regional-sea-level/')) {
                    // This must be checked before the generic `region` branch:
                    // `/regional-sea-level/` itself contains "region".  Letting
                    // it fall through to that boundary-only layer set
                    // `filled: false`, which made every SLA map frame invisible.
                    const isContour = path.includes('/contour/');
                    deckLayers.push(new GeoJsonLayer({
                        id: `regional-sea-level-${isContour ? 'contour' : 'grid'}-${i}`,
                        data: currentData,
                        onClick: clickHandler,
                        getFillColor: d => [
                            d?.properties?.R ?? 128,
                            d?.properties?.G ?? 128,
                            d?.properties?.B ?? 128,
                            isContour ? 230 : 245,
                        ],
                        getLineColor: [0, 0, 0, 0],
                        filled: true,
                        stroked: false,
                        pickable: !isContour,
                        autoHighlight: !isContour,
                        highlightColor: [255, 255, 255, 55],
                        wrapLongitude: false,
                        parameters: { depthTest: false },
                    }));
                } else if (path.includes("EEZ")||path.includes("region")||path.includes("Outline")||path.includes("outline")){
                     deckLayers.push(new GeoJsonLayer({ id: `boundary-${i}`, data: currentData, filled: false,
                      stroked: true,
                      lineWidthScale: 20,
                      lineWidthMinPixels: 2,/* ... other props */ }));
                } else if (path.includes("GAUS")){
                     deckLayers.push(new GeoJsonLayer({ 
                        id: `gaus-${i}`, 
                        data: currentData, 
                        onClick: clickHandler, 
                        getFillColor: d => hexToRgb(d?.properties?.fill || '#808080'), 
                        getLineColor: d => hexToRgb(d?.properties?.fill || '#808080'),
                        filled: true,
                        stroked: true,
                        lineWidthScale: 20,
                        lineWidthMinPixels: 2,
                        getLineWidth: 2,
                        pickable: true
                    }));
                    
                } else if (path.includes("mhw")) {
                     const isContours = path.includes("contours");
                     const isGridded = path.includes("gridded");
                     deckLayers.push(new GeoJsonLayer({ 
                        id: `mhw-${i}`, 
                        data: currentData, 
                        onClick: clickHandler, 
                        getFillColor: d => d?.properties?.R != null
                            ? [d.properties.R, d.properties.G ?? 128, d.properties.B ?? 128, 245]
                            : hexToRgb(d?.properties?.fill || '#808080'),
                        getLineColor: d => d?.properties?.R != null
                            ? [d.properties.R, d.properties.G ?? 128, d.properties.B ?? 128, 255]
                            : hexToRgb(d?.properties?.fill || '#808080'),
                        filled: true,
                        stroked: isContours,
                        getLineWidth: isContours ? 2 : 0,
                        lineWidthScale: 20,
                        lineWidthMinPixels: 2,
                        pickable: isGridded, // Only gridded layer triggers hover/click events
                        autoHighlight: isGridded
                    }));
                } else if (path.includes('/wave-hindcast/')) {
                    // WaveWatch III hindcast cells are emitted with the
                    // archive palette already attached. Gridded cells are
                    // interactive so a user can open the matching history;
                    // contours remain a clean, non-interactive surface.
                    deckLayers.push(new GeoJsonLayer({
                        id: `wave-hindcast-${i}`,
                        data: currentData,
                        getFillColor: d => [
                            d?.properties?.R ?? 128,
                            d?.properties?.G ?? 128,
                            d?.properties?.B ?? 128,
                            255,
                        ],
                        getLineColor: d => [
                            d?.properties?.R ?? 128,
                            d?.properties?.G ?? 128,
                            d?.properties?.B ?? 128,
                            0,
                        ],
                        filled: true,
                        stroked: false,
                        pickable: path.includes('/gridded/') && Boolean(clickHandler),
                        onClick: clickHandler,
                        autoHighlight: false,
                        parameters: { depthTest: false },
                    }));
                }else if (path.includes("/ekman/")) {
                    // Ekman upwelling tiles — always Indo-Pacific (lon 0-360, no wrap)
                    deckLayers.push(new GeoJsonLayer({
                        id: `ekman-grid-${i}`,
                        data: currentData,
                        onClick: clickHandler,
                        getFillColor: d => {
                            if (d?.properties?.R != null) {
                                return [d.properties.R, d.properties.G ?? 128, d.properties.B ?? 128, 255];
                            }
                            const rgb = hexToRgb(d?.properties?.fill || '#808080');
                            return [...rgb, 255];
                        },
                        getLineColor: d => {
                            if (d?.properties?.R != null) {
                                return [d.properties.R, d.properties.G ?? 128, d.properties.B ?? 128, 255];
                            }
                            return hexToRgb(d?.properties?.fill || '#808080');
                        },
                        filled: true,
                        stroked: false,
                        getLineWidth: 0,
                        wrapLongitude: false,
                        pickable: true,
                        autoHighlight: true,
                        parameters: { depthTest: false }
                    }));
                } else if (path.includes('/jsonFiles/apcc/')) {
                    const isContour = path.includes('/contour/');
                    const isIndoPacific = path.includes('/indo/');
                    deckLayers.push(new GeoJsonLayer({
                        id: `apcc-${isContour ? 'contour' : 'cells'}-${i}`,
                        data: currentData,
                        onClick: clickHandler,
                        getFillColor: d => {
                            if (d?.properties?.R != null) {
                                return [d.properties.R, d.properties.G ?? 128, d.properties.B ?? 128, isContour ? 220 : 245];
                            }
                            return [...hexToRgb(d?.properties?.fill || '#808080'), isContour ? 220 : 245];
                        },
                        getLineColor: d => d?.properties?.R != null
                            ? [d.properties.R, d.properties.G ?? 128, d.properties.B ?? 128, 255]
                            : hexToRgb(d?.properties?.fill || '#808080'),
                        filled: true,
                        stroked: false,
                        getLineWidth: 0,
                        wrapLongitude: !isIndoPacific,
                        pickable: !isContour,
                        autoHighlight: !isContour,
                        parameters: { depthTest: false },
                    }));
                } else if (path.includes('/ocean-color/')) {
                    const isContour = path.includes('/contour/');
                    deckLayers.push(new GeoJsonLayer({
                        id: `ocean-color-${isContour ? 'contour' : 'grid'}-${i}`,
                        data: currentData,
                        onClick: clickHandler,
                        getFillColor: d => [
                            d?.properties?.R ?? 128,
                            d?.properties?.G ?? 128,
                            d?.properties?.B ?? 128,
                            isContour ? 225 : 235,
                        ],
                        getLineColor: [0, 0, 0, 0],
                        filled: true,
                        stroked: false,
                        pickable: !isContour,
                        autoHighlight: !isContour,
                        highlightColor: [255, 255, 255, 70],
                        wrapLongitude: true,
                        parameters: { depthTest: false },
                    }));
                } else if (path.includes('/coral-reef-watch/')) {
                    // Daily CRW products are published as numeric FlatGeobuf
                    // values.  They require their own NOAA product palettes,
                    // rather than the pre-coloured legacy BAA renderer below.
                    // Paths are scope-aware: .../palau/native/{product}/{date}
                    // or .../pacific/{resolution}/{product}/{date}.  The
                    // product is therefore the directory immediately before
                    // the dated FlatGeobuf file, not the first directory.
                    const productKey = path.split('/').filter(Boolean).slice(-2)[0] || 'dhw';
                    deckLayers.push(new GeoJsonLayer({
                        id: `crw-daily-${productKey}-${i}`,
                        data: currentData,
                        onClick: clickHandler,
                        getFillColor: d => crwColor(productKey, d?.properties?.value),
                        getLineColor: d => crwColor(productKey, d?.properties?.value, 255),
                        filled: true,
                        stroked: false,
                        pickable: true,
                        autoHighlight: false,
                        wrapLongitude: true,
                        parameters: { depthTest: false },
                    }));
                } else if (/\/forecasts\/(?:gfs|cfs)\/wind\//.test(path)) {
                     const isIndoPacific = path.includes('/indo/');
                     const isContour = path.includes('/contour/');
                     deckLayers.push(new GeoJsonLayer({
                        id: `forecast-wind-magnitude-${i}`,
                        data: currentData,
                        dataTransform: data => data?.features
                          ? { ...data, features: data.features.filter(isRenderableForecastWindFeature) }
                          : data,
                        onClick: clickHandler,
                        getFillColor: d => windColor(d?.properties?.wind_speed ?? d?.properties?.wind ?? d?.properties?.value ?? d?.properties?.speed),
                        filled: true,
                        stroked: false,
                        wrapLongitude: !isIndoPacific,
                        // Contours are visual context only; keeping them out
                        // of picking prevents the bright hover flash that can
                        // obscure adjacent gridded cells and wind particles.
                        pickable: !isContour,
                        autoHighlight: !isContour,
                        parameters: { depthTest: false }
                     }));
                } else if (path.includes("OISST") || path.includes("oisst") || path.includes("/forecasts/")){
                     const isIndoPacific = path.includes("IndoPacific") || path.includes("/indo/");
                     const isContour = path.includes("Contour") || path.includes("CONTOUR") || path.includes("/contour/");
                     const isForecast = path.includes("/forecasts/");
                     deckLayers.push(new GeoJsonLayer({ 
                        id: `${isForecast ? 'Forecast' : 'OISST'}-${isIndoPacific ? 'indo' : 'local'}-${isContour ? 'contour' : 'grid'}-${i}`, 
                        data: currentData, 
                        onClick: clickHandler, 
                        getFillColor: d => {
                            if (d?.properties?.R != null) {
                                return [d.properties.R, d.properties.G ?? 128, d.properties.B ?? 128, isContour ? 220 : 255];
                            }
                            const rgb = hexToRgb(d?.properties?.fill || '#808080');
                            return [...rgb, isContour ? 220 : 255];
                        },
                        getLineColor: d => {
                            if (d?.properties?.R != null) {
                                return [d.properties.R, d.properties.G ?? 128, d.properties.B ?? 128, 255];
                            }
                            return hexToRgb(d?.properties?.fill || '#808080');
                        },
                        filled: true,
                        // Gridded Indo-Pacific (~37k cells): no stroke (performance).
                        // Contours: light stroke locally; Indo contours stay fill-only.
                        stroked: isContour && !isIndoPacific,
                        getLineWidth: isContour ? 1 : 0,
                        lineWidthMinPixels: isContour ? 1 : 0,
                        // Indo-Pacific tiles use lon 93..245 (same as CRW Pacific). Do NOT wrapLongitude
                        // on those — wrapping breaks east-of-dateline cells. Rely on MapView({repeat:true}).
                        wrapLongitude: !isIndoPacific,
                        pickable: !isContour,
                        autoHighlight: !isContour,
                        parameters: { depthTest: false }
                    }));
                    
                } else if (path.includes("rmiLand") || path.endsWith("/palau.json") || path.includes("Shoreline") || path.includes("shoreline")) {
                     deckLayers.push(new GeoJsonLayer({
                        id: `forecast-land-${i}`,
                        data: currentData,
                        filled: true,
                        stroked: true,
                        getFillColor: [221, 221, 221, 255],
                        getLineColor: [102, 102, 102, 255],
                        getLineWidth: 1,
                        lineWidthMinPixels: 1,
                        pickable: false,
                        wrapLongitude: true,
                        parameters: { depthTest: false }
                     }));
                } 
                else if (path.includes('/jsonFiles/cmip6/')) {
                    const isContour = path.includes('/contour.fgb');
                    deckLayers.push(new GeoJsonLayer({
                        id: `cmip6-manifest-${isContour ? 'contour' : 'cells'}-${i}`,
                        data: currentData,
                        onClick: clickHandler,
                        getFillColor: d => hexToRgb(d?.properties?.fill || '#808080'),
                        getLineColor: d => hexToRgb(d?.properties?.fill || '#808080'),
                        filled: true,
                        stroked: false,
                        pickable: !isContour,
                        autoHighlight: !isContour,
                        highlightColor: [255, 255, 255, 55],
                        wrapLongitude: false,
                        parameters: { depthTest: false },
                    }));
                }
                else if (path.includes("ssp")||(path.includes("tos_Omon") )){
                     deckLayers.push(new GeoJsonLayer({ 
                        id: `cmip6-${i}`, 
                        data: currentData, 
                        onClick: clickHandler, 
                        getFillColor: d => hexToRgb(d?.properties?.fill || '#808080'), 
                        getLineColor: d => hexToRgb(d?.properties?.fill || '#808080'),
                        // lineWidthScale: 20,
                        // lineWidthMinPixels: 2,
                        // getLineWidth: 2,
                        filled: true,
                        stroked: true,
                        pickable: true,
                        autoHighlight: true
                    }));
                    
                } else if (path.includes("CRW") || path.includes("BAA") || path.includes("CFS")) {
                    deckLayers.push(new GeoJsonLayer({
                        id: path.includes("CFS") ? `cfs-${i}` : `crw-${i}`,
                        data: currentData,
                        onClick: clickHandler,
                        getFillColor: d => [d?.properties?.R ?? 128, d?.properties?.G ?? 128, d?.properties?.B ?? 128],
                        getLineColor: d => [d?.properties?.R ?? 128, d?.properties?.G ?? 128, d?.properties?.B ?? 128],
                        filled: true,
                        pickable: true,
                        autoHighlight: true
                    }));
                } else if (path.includes("access/") || path.includes("ACCESS")) {
                    const geojsonData = parseAccessSData(currentData);
                    if (geojsonData) {
                        deckLayers.push(new GeoJsonLayer({
                            id: `access-s-${i}`,
                            data: geojsonData,
                            onClick: clickHandler,
                            getFillColor: d => {
                                const val = d.properties.rain;
                                if (val === undefined || val === null) return [128, 128, 128, 100];
                                if (val < -10) return [165, 0, 38, 200];
                                if (val < -2) return [244, 109, 67, 200];
                                if (val < 2) return [255, 255, 191, 150]; // Near normal
                                if (val < 10) return [116, 173, 209, 200];
                                return [49, 54, 149, 200];
                            },
                            getLineColor: [0, 0, 0, 100],
                            filled: true, stroked: true, getLineWidth: 1, lineWidthMinPixels: 1, pickable: true, autoHighlight: true
                        }));
                    }
                } else {
                    deckLayers.push(new GeoJsonLayer({ id: `default-${i}`, data: currentData, onClick: clickHandler, getFillColor: data => [data?.properties?.R ?? 128, data?.properties?.G ?? 128, data?.properties?.B ?? 128], getLineColor: data => [data?.properties?.R ?? 128, data?.properties?.G ?? 128, data?.properties?.B ?? 128], /* ... other props */ }));
                }
            }
        }
    } catch (err) {
        console.error(`MapLoader: Error generating layers`, err);
        return [];
    }

    if (showWindParticles && windRenderMode === 'vectors' && visibleVectorSources.length) {
      deckLayers.push(new PathLayer({
        id: 'forecast-wind-static-vectors',
        data: staticWindVectors(visibleVectorSources, isLocalWindView),
        getPath: d => d.path,
        getColor: d => [...windColor(d.speed).slice(0, 3), 210],
        getWidth: 1.25,
        widthUnits: 'pixels',
        widthMinPixels: 1,
        widthMaxPixels: 2,
        jointRounded: true,
        capRounded: true,
        pickable: false,
        parameters: { depthTest: false },
      }));
    }

    if (showWindParticles && windRenderMode === 'barbs' && windBarbGeometry.strokes.length) {
      deckLayers.push(new PathLayer({
        id: 'forecast-wind-noaa-barb-shafts',
        data: windBarbGeometry.strokes,
        getPath: d => d.path,
        getColor: [0, 0, 0, 235],
        getWidth: d => d.calm ? 1.5 : 1.1,
        widthUnits: 'pixels',
        widthMinPixels: 0.8,
        jointRounded: true,
        capRounded: true,
        pickable: false,
        parameters: { depthTest: false },
      }));
      if (windBarbGeometry.pennants.length) {
        deckLayers.push(new PolygonLayer({
          id: 'forecast-wind-noaa-barb-pennants',
          data: windBarbGeometry.pennants,
          getPolygon: d => d.polygon,
          getFillColor: [0, 0, 0, 235],
          stroked: false,
          filled: true,
          pickable: false,
          parameters: { depthTest: false },
        }));
      }
    }

    if (showWindParticles && windRenderMode === 'particles' && flowParticles.length) {
      // Overlapping tail sections approximate a continuously tapered stroke:
      // thin/dim at the tail, smoothly accumulating into the lead segment.
      const trailSections = [
        { start: 0, width: 0.55, alpha: 58 },
        { start: 0.34, width: 0.8, alpha: 86 },
        { start: 0.58, width: 1.05, alpha: 122 },
        { start: 0.77, width: 1.25, alpha: 166 },
      ];
      trailSections.forEach(({ start, width, alpha }, sectionIndex) => {
        deckLayers.push(new PathLayer({
          id: `forecast-wind-particle-trail-${sectionIndex}`,
          data: flowParticles,
          getPath: d => {
            const path = d.trail.slice(Math.floor(d.trail.length * start));
            const head = d.trail[d.trail.length - 1];
            const visualLengthScale = 1;
            return path.map(point => [
              head[0] + (point[0] - head[0]) * visualLengthScale,
              head[1] + (point[1] - head[1]) * visualLengthScale,
            ]);
          },
          getColor: d => [220, 250, 255, Math.round(alpha * Math.min(1, d.age / 6, (d.life - d.age) / 8))],
          getWidth: width,
          widthUnits: 'pixels',
          widthMinPixels: 0.5,
          jointRounded: true,
          capRounded: true,
          pickable: false,
          parameters: { depthTest: false }
        }));
      });
      deckLayers.push(new ScatterplotLayer({
        id: 'forecast-wind-moving-particles',
        data: flowParticles,
        getPosition: d => d.position,
        // A denser, bright leading cap gives each streamline a clear direction
        // of travel while its trail stays fine and unobtrusive.
        getRadius: d => 1.1 + d.magnitude * 0.2,
        radiusUnits: 'pixels',
        getFillColor: d => [242, 253, 255, Math.round(210 * Math.min(1, d.age / 2.5, (d.life - d.age) / 4))],
        stroked: false,
        pickable: false,
        parameters: { depthTest: false }
      }));
    }

    // Update the ref with the latest valid layers
    lastValidLayers.current = deckLayers;

    return deckLayers;
  }, [mapDataSource, filePath, onClick, time, flowParticles, showWindParticles, windRenderMode, waveVectorMode, mapZoom, settledViewState, visibleVectorSources, isLocalWindView, windBarbGeometry, retainPreviousLayers]);

  return { layers, isLoading, data: mapDataSource };
};
